// TODO: update CSS to use `input:checked` currently
// this only works for controlled components.
import * as React from 'react';
import { useFocus } from '../../hooks';
import Icon, { IconVariant } from '../Icon';
import Label from './Label';
import { FormFieldStatus, statusStyle } from './appearance';
import {
  AlignItems,
  Color,
  FontWeight,
  JustifyContent,
  SpaceScale,
} from '../../theme/tokens';
import Box from '../Box';
import { Text } from '../typography';
import { Space } from '../layout';
import VisuallyHidden from '../VisuallyHidden';
import { useTheme } from '../../theme';
import { CSSRulesFunction, ResponsiveValue } from '../../theme/types';
import { isFunction } from '../../utils';

type HTMLAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  keyof AppearanceProps
>;

export enum RadioSize {
  'Tight' = 'Tight',
  'Normal' = 'Normal',
}

interface AppearanceProps {
  status?: FormFieldStatus;
  disabled?: boolean;
  outline?: boolean;
  size?: RadioSize;
}

interface InteractionState {
  focused?: boolean;
  checked?: boolean;
}

interface InternalRadioProps extends AppearanceProps, InteractionState {}

export interface RadioProps extends InternalRadioProps, HTMLAttributes {
  name: string;
  children?: ((focused: boolean) => React.ReactNode) | React.ReactNode;
}

const radioLabelStyle: CSSRulesFunction<InternalRadioProps> = (
  theme,
  props
) => {
  const { status, disabled, focused, outline, size } = props;
  const colors = statusStyle(theme, { status, disabled });
  const {
    baseBorderColor,
    focusBorderColor,
    textColor,
    backgroundColor,
  } = colors;

  const sizeMap = {
    Tight: 2,
    Normal: 3,
  };

  const padding = theme.responsiveSpacing(
    sizeMap[size || 'Normal'] as ResponsiveValue<SpaceScale>,
    ['padding']
  );

  let boxShadow = '';

  if (outline) {
    boxShadow = `inset 0 0 0 1px ${baseBorderColor}`;
  }

  if (focused && outline) {
    boxShadow = `inset 0 0 0 2px ${focusBorderColor}`;
  }

  return [
    {
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      backgroundColor,
      color: focused ? theme.color(Color.Secondary_400) : textColor,
      boxShadow,
      ...(outline && {
        '&:hover': !focused && {
          boxShadow: `inset 0 0 0 1px ${focusBorderColor}`,
        },
      }),
    },
    padding,
    theme.radius(1),
  ];
};

const radioIconColor: CSSRulesFunction<InternalRadioProps> = (theme, props) => {
  const { disabled, checked, outline } = props;

  if (!disabled && !checked && !outline) {
    return [
      {
        color: theme.color(Color.ColdGrey_600),
        '&:hover': {
          color: theme.color(Color.Secondary_400),
        },
      },
    ];
  }

  return [
    {
      color: disabled
        ? theme.color(Color.ColdGrey_200)
        : checked
        ? theme.color(Color.Secondary_400)
        : theme.color(Color.NeutralGrey_600),
    },
  ];
};

const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(
  props,
  ref
) {
  const {
    status,
    children,
    onFocus,
    onBlur,
    disabled,
    checked,
    outline,
    size,
    ...rest
  } = props;
  const { handleFocus, handleBlur, focused } = useFocus(onFocus, onBlur);
  const { theme } = useTheme();

  return (
    <Label
      for={props.id}
      css={radioLabelStyle(theme, { focused, status, disabled, outline, size })}
    >
      <VisuallyHidden>
        <input
          {...rest}
          // defaultChecked prop should not be passed if Radio is controlled
          // Inputs must either be controlled or not, not both
          // https://reactjs.org/link/controlled-components
          {...(!isFunction(rest.onChange) && { defaultChecked: true })}
          type="radio"
          checked={checked}
          aria-required={rest.required}
          aria-invalid={status === FormFieldStatus.Error}
          disabled={disabled}
          aria-disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={ref}
        />
      </VisuallyHidden>
      <Box
        display="inline-flex"
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        css={radioIconColor(theme, props)}
      >
        <Icon
          size={6}
          name={checked ? 'radio_selected' : 'radio_default'}
          variant={IconVariant.Inherit}
        />
      </Box>
      {children && !isFunction(children) && (
        <>
          <Space size={3} direction="horizontal" />
          <Text
            fontWeight={focused ? FontWeight.SemiBold : FontWeight.Normal}
            color={disabled ? Color.ColdGrey_200 : undefined}
          >
            {children}
          </Text>
        </>
      )}
      {children && isFunction(children) && children(focused)}
    </Label>
  );
});

export default Radio;
